/**
 * This class does most of the heavy lifting for reading and writing data in Firestore. It exposes main CRUD operations
 * for get, getAll, save, query, and delete
 */
class FirestoreModel {
  /**
   * Main constructor
   * @param firestore     Initialized instance of Firestore
   * @param functions     Initialized instance of Firebase Functions
   * @param collection    The name of the Firestore collection for this model
   * @param schema        The Yup schema of this model
   * @param queries       The object containing methods for special queries of this model's collection
   * @param callables     The object containing methods for calling https-callable Cloud Function
   * @param mixins        The object containing helper-like methods to attach to each converted model from this schema
   * @param convertDates  The function for converting Timestamp objects to JS Dates whenever models are inflated from Firestore
   */
  constructor(firestore, functions, collection, schema, queries, callables, mixins, convertDates) {
    this.firestore = firestore;
    this.functions = functions;
    this.collection = collection;
    this.schema = schema;
    this.convertDates = convertDates;
    this.mixins = mixins;

    if (queries) {
      Object.keys(queries).forEach(key => {
        this[key] = async function () {
          const query = queries[key](...arguments);
          return this.query(query.where, query.orderBy, query.startAfter, query.limit);
        }.bind(this);
      });
    }

    if (callables) {
      Object.keys(callables).forEach(key => {
        this[key] = async function () {
          const {
            data,
            onResponse
          } = callables[key](...arguments);
          const response = await this.functions.httpsCallable(`${this.collection}-${key}`)(data);

          if (onResponse) {
            onResponse(response.data);
          }

          return response.data;
        }.bind(this);
      });
    }
  }
  /**
   * Validates the schema of the object passed in
   * @param object    The object to validate
   * @returns true or throws an error
   */


  validate(object) {
    return this.schema.validate(object, {
      abortEarly: false,
      stripUnknown: true
    });
  }
  /**
   * Writes an object to Firestore. All records automagically get "createdAt" and "updatedAt" values added to the saved
   * object.
   * @param object    The object to write
   * @param merge     If true, the object will be merged on top of an existing record instead of overwriting it
   * @param transaction   Optional Firestore transaction to use for the update operation
   * @returns Promise that resolves to the object stored in Firestore and adds the document id for new records
   */


  async save(object, merge = false, transaction = null) {
    const updatedAt = new Date();
    let objectId = object.id;
    const collectionRef = this.firestore.collection(this.collection);
    const validatedObject = await this.validate(object);
    validatedObject.createdAt = object.createdAt ? object.createdAt : updatedAt;
    validatedObject.updatedAt = updatedAt;

    if (objectId) {
      const ref = collectionRef.doc(objectId);
      await (transaction ? transaction.set(ref, validatedObject) : ref.set(validatedObject, {
        merge
      }));
    } else {
      const docRef = await collectionRef.add(validatedObject);
      objectId = docRef.id;
    }

    const retVal = {
      id: objectId,
      ...validatedObject
    }; // Mixins are helper methods attached to each returned object. `this` has no meaningful
    // context within these methods. Each method is given a reference to the attached object
    // as the first parameter when invoked -- this can be used to access model data within
    // the scope of a mixin implementation.

    const mixins = this.mixins;

    if (mixins) {
      Object.keys(mixins).forEach(key => {
        retVal[key] = function () {
          return mixins[key](retVal, ...arguments);
        };
      });
    }

    return retVal;
  }
  /**
   * Fetches a single object from this Firestore collection
   * @param objectId  The id of the object to fetch
   * @param transaction Optional Firestore transaction to pass for the 'get' operation: https://firebase.google.com/docs/firestore/manage-data/transactions
   * @returns Promise that resolves to the document, if it exists, or null
   */


  async get(objectId, transaction = null) {
    const ref = this.firestore.collection(this.collection).doc(objectId);
    const docSnapshot = await (transaction ? transaction.get(ref) : ref.get());

    if (!docSnapshot.exists) {
      return null;
    }

    return _convertFromFirestoreDoc(docSnapshot, this.convertDates, this.mixins);
  }
  /**
   * Fetches the entire collection
   * @param orderBy   OrderBy parameters to pass to the Firestore query when fetching all the records
   * @returns {Promise<*>}
   */


  async getAll(orderBy) {
    return this.query(null, orderBy);
  }
  /**
   * Deletes and object from Firestore
   * @param objectId  The id of the object to delete
   * @returns {Promise<WriteResult>}
   */


  delete(objectId) {
    return this.firestore.collection(this.collection).doc(objectId).delete();
  }
  /**
   * Performs a query on the Firestore collection
   * @param where     Parameters to pass to a Firestore "where" query
   * @param orderBy   Parameters to pass to a Firestore "orderBy" query
   * @param startAfter    The orderBy value to start after, for pagination. Only applied if orderBy also specified
   * @param limit     Maximum number of records to return. Only applied if orderBy also specified
   * @returns {Promise<*>}
   */


  query(where, orderBy, startAfter, limit) {
    let query = this.firestore.collection(this.collection);

    if (where) {
      if (where[0] instanceof Array) {
        where.forEach(item => {
          query = query.where(...item);
        });
      } else {
        query = query.where(...where);
      }
    }

    if (orderBy) {
      query = query.orderBy(...orderBy);

      if (startAfter) {
        query = query.startAfter(startAfter);
      }

      if (limit) {
        query = query.limit(limit);
      }
    }

    return runQuery(query, this.convertDates, this.mixins);
  }

}
/**
 * Transforms raw output from a Firestore document to a more "consumable" object. By default createdAt and updatedAt
 * values get converted to normal JS Dates, and if a convertDates function has been supplied to the model it will run
 * the custom convert function as well.
 * @param firestoreDocument The document from Firestore
 * @param convertDates      Custom method passed into the model for transforming other date objects inside of the model
 * @param mixins            Helper methods to attach to each returned object
 * @returns {*|{}}
 * @private
 */


function _convertFromFirestoreDoc(firestoreDocument, convertDates, mixins) {
  let retVal = firestoreDocument.data() || {}; // Special case for GeoFirestore records

  if (retVal.d && retVal.g && retVal.l) {
    // then we assume we are doing a raw firestore query on a geofirestore structured document, so we pull
    // out the "data" to the top level. This is needed for when we search for deals WITHOUT specifying an
    // exact location to search by. See ecCrudOperations.searchDeals() and
    // https://github.com/geofirestore/geofirestore-js#data-structure
    retVal = retVal.d;
  }

  retVal.id = firestoreDocument.id; // convert timestamps

  if (retVal.createdAt && retVal.createdAt.toDate) {
    retVal.createdAt = retVal.createdAt.toDate();
  }

  if (retVal.updatedAt && retVal.updatedAt.toDate) {
    retVal.updatedAt = retVal.updatedAt.toDate();
  }

  if (convertDates) {
    retVal = convertDates(retVal);
  }

  if (mixins) {
    Object.keys(mixins).forEach(key => {
      retVal[key] = function () {
        return mixins[key](retVal, ...arguments);
      };
    });
  }

  return retVal;
}
/**
 * Queries Firestore and converts the resulting documents
 * @param query         The query to perform on the Firebase collection
 * @param convertDates  The custom function for transforming internal Timestamps to JS Dates
 * @param mixins        Helper methods to attach to each returned object
 * @returns {Promise<*>}
 * @private
 */


async function runQuery(query, convertDates, mixins) {
  const querySnapshot = await query.get();
  return querySnapshot.docs.map(doc => _convertFromFirestoreDoc(doc, convertDates, mixins));
}

module.exports = {
  FirestoreModel,
  runQuery
};